import {withProps, mapProps} from 'recompose'

import compose from 'lodash/flowRight'
import * as React from 'react'
import {Suspense, useEffect} from 'react'
import {connect} from 'react-redux'
import {graphql} from 'react-relay'

import withStatusSubscription from '../../../../../../hocs/withStatusSubscription'
import {useRefetchableLazyLoadQuery} from '../../../../../../hooks/relay/useRefetchableLazyLoadQuery'
import type {BranchProps} from '../../../../../../hooks/routes'
import {withBranchProps} from '../../../../../../hooks/routes'
import type {State} from '../../../../../../reducers/types'
import {getBuildStatsLocator} from '../../../../../../rest/locators'
import {getBuildType, getStatus, isArchivedProject} from '../../../../../../selectors'
import {getStatusRequest} from '../../../../../../types'
import {subscribeOnBuildTypeEvents} from '../../../../../../utils/subscriber'
import * as SubscriptionEvents from '../../../../../../utils/subscriptionEvents'
import BuildTypeInfoWrapper from '../../../../../common/BuildTypeInfoWrapper/BuildTypeInfoWrapper'
import type {VisibleProps} from '../../../../../common/Visible/Visible.hocs'
import {withVisibility} from '../../../../../common/Visible/Visible.hocs'

import type {BuildTypeInfoPanelQuery} from './__generated__/BuildTypeInfoPanelQuery.graphql'
import BuildTypeInfoPanel, {TIMELINE_STAT_COUNT} from './BuildTypeInfoPanel'
import type {
  OwnProps,
  ComposeProps,
  StateProps,
  Props,
  InnerProps,
} from './BuildTypeInfoPanel.types'

import styles from './BuildTypeInfoPanel.css'

const mapStateToProps = (
  state: State,
  {buildTypeId, branch, projectId}: OwnProps & ComposeProps,
): StateProps => {
  const buildType = getBuildType(state, buildTypeId)
  const locator = getBuildStatsLocator(buildTypeId, branch, TIMELINE_STAT_COUNT)
  const queuedCount = getStatus(state, getStatusRequest('bt', buildTypeId, branch))?.queued ?? 0
  return {
    locator,
    name: buildType?.name,
    internalId: buildType?.internalId,
    archivedProject: isArchivedProject(state, projectId),
    queuedCount,
  }
}

const query = graphql`
  query BuildTypeInfoPanelQuery($locator: String!) {
    builds(locator: $locator) {
      ...BuildTypeInfoPanelFragment
    }
  }
`

const Events = [
  SubscriptionEvents.BUILD_STARTED,
  SubscriptionEvents.BUILD_FINISHED,
  SubscriptionEvents.BUILD_CHANGED_STATUS,
  SubscriptionEvents.BUILD_INTERRUPTED,
  SubscriptionEvents.BUILD_REMOVED,
]
function BuildTypeInfoPanelFetcher(props: InnerProps) {
  const {locator, blockUpdates, internalId} = props
  const [{builds}, refetch] = useRefetchableLazyLoadQuery<BuildTypeInfoPanelQuery>(query, {
    locator,
  })
  useEffect(
    () =>
      !blockUpdates && internalId != null
        ? subscribeOnBuildTypeEvents(internalId, Events, refetch)
        : undefined,
    [internalId, blockUpdates, refetch],
  )
  return <BuildTypeInfoPanel {...props} buildsKey={builds} />
}

function BuildTypeInfoPanelWrapper(props: InnerProps) {
  return (
    <Suspense
      fallback={
        <BuildTypeInfoWrapper>
          <BuildTypeInfoPanel {...props} loading />
        </BuildTypeInfoWrapper>
      }
    >
      <BuildTypeInfoWrapper>
        <BuildTypeInfoPanelFetcher {...props} />
      </BuildTypeInfoWrapper>
    </Suspense>
  )
}

export default compose(
  withBranchProps,
  withVisibility(true, undefined, undefined, styles.cell),
  withProps(({isVisible}: OwnProps & BranchProps & VisibleProps) => ({
    blockUpdates: !isVisible,
  })),
  connect(mapStateToProps),
  withStatusSubscription(),
  mapProps(({isVisible, ...restProps}: Props) => restProps),
  React.memo,
)(BuildTypeInfoPanelWrapper)
